import { useMemo, useState } from 'react';
import { t } from '@app/i18n';
import { Card, Chip, LocalSearchField } from '@zarya/ui';
import type { components } from '@app/api/types';
import { IdentityStateBadge } from '@app/main/Identities/IdentityStateBadge';
import { useMultiSite } from '@app/main/Sites/Sites.api';
import s from './IdentityListPane.module.css';

export type Identity = components['schemas']['Identity'];

export interface IdentityListPaneProps {
  identidades: Identity[];
  selecionada: string | null;
  onEscolher: (id: string) => void;
  carregando?: boolean;
  /** Explicação ⓘ do card — cada tela diz o que a escolha faz nela. */
  tip?: string;
  /**
   * Motivo pelo qual a identidade **não** pode ser escolhida nesta tela, ou
   * `null` quando pode. A linha fica desabilitada e o motivo aparece no lugar
   * do contrato, em vermelho (T14/FR-071: "aprove a primeira release antes").
   * Sem a prop, toda identidade é escolhível (T13).
   */
  motivoIndisponivel?: (identidade: Identity) => string | null;
}

/**
 * Função, não constante: um texto de módulo é avaliado no import e congela o
 * idioma. Como ela também é **valor padrão de prop**, o `tip = tipPadrao()` no
 * destructuring roda a cada render — que é o que se quer.
 */
const tipPadrao = () => t('common.identityPane.tip');

/**
 * Lista de identidades à esquerda (item 7.1 do roteiro do PO, 28/08): nome,
 * papel, contrato vigente e estado, uma por linha, com o detalhe ao lado.
 * Substitui o combobox do cabeçalho — a pessoa vê o conjunto inteiro e escolhe
 * comparando, sem abrir e fechar um menu.
 *
 * Nasceu em T13 (`ContractIdentityList`) e é compartilhada com T14 desde 28/08
 * (pedido do mrolim: "a T14 seleciona a identidade do mesmo jeito que a T13").
 * O que muda entre as telas é só o ⓘ e quais identidades ficam indisponíveis —
 * a FR-071 desabilita em T14 as que não têm contrato ativo; em T13 todas abrem.
 *
 * `listbox`/`option`: o E2E e o leitor de tela continuam achando a identidade
 * por `option` com o nome começando pelo slug.
 */
export function IdentityListPane({
  identidades,
  selecionada,
  onEscolher,
  carregando = false,
  tip = tipPadrao(),
  motivoIndisponivel,
}: IdentityListPaneProps) {
  const [filter, setFiltro] = useState('');

  // O SITE entra aqui e não em cada tela porque este painel é UM componente para
  // TRÊS: Contracts (T13), Identity Access (T14) e Runtime Status (T15). Foi o censo
  // de 11/09 que o mostrou — o mrolim tinha visto T13 e T14; o T15 veio junto.
  //
  // `useMultiSite` e não uma prop: a revelação progressiva é a mesma regra de T4/T6 e
  // não é decisão de quem chama. Com um site só, nada aparece — um rótulo que diz
  // sempre a mesma coisa é ruído em cada linha da lista.
  const multiSite = useMultiSite();

  const visiveis = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return identidades;
    return identidades.filter((identidade) =>
      [identidade.slug, identidade.image_basename, identidade.role]
        .filter(Boolean)
        .some((campo) => String(campo).toLowerCase().includes(term)),
    );
  }, [filter, identidades]);

  return (
    <Card
      title={t('common.identityPane.title')}
      subtitle={
        carregando
          ? undefined
          : t('common.identityPane.count', { n: visiveis.length, total: identidades.length })
      }
      tip={tip}
      className={s.listaCard}
    >
      <LocalSearchField
        value={filter}
        onChange={setFiltro}
        placeholder={t('common.identityPane.filter')}
        helperText={t('common.identityPane.filter.help')}
      />
      {carregando ? <p className={s.carregando}>{t('common.identityPane.loading')}</p> : null}
      {!carregando && visiveis.length === 0 ? (
        <p className={s.muted}>{t('common.identityPane.empty')}</p>
      ) : null}
      <ul className={s.lista} role="listbox" aria-label={t('common.identityPane.listLabel')}>
        {visiveis.map((identidade) => {
          const nome = identidade.slug ?? identidade.image_basename ?? identidade.id;
          const ativa = identidade.id === selecionada;
          const motivo = motivoIndisponivel?.(identidade) ?? null;
          const choose = () => {
            if (!motivo) onEscolher(identidade.id);
          };
          return (
            <li
              key={identidade.id}
              role="option"
              aria-selected={ativa}
              aria-disabled={motivo ? true : undefined}
              aria-label={`${nome}${identidade.role ? ` · ${identidade.role}` : ''}${multiSite && identidade.site_slug ? ` · ${t('common.identityPane.site', { site: identidade.site_slug })}` : ''}`}
              className={[s.item, ativa ? s.itemOn : ''].filter(Boolean).join(' ')}
              tabIndex={motivo ? -1 : 0}
              onClick={choose}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  choose();
                }
              }}
            >
              <span className={s.itemTopo}>
                <b className={s.itemNome}>{nome}</b>
                {identidade.role ? <Chip>{identidade.role}</Chip> : null}
                {/* O SLUG do site, como em T4 e T6 — e não o `name`. A razão está
                    escrita no `IdentitiesPage.tsx`: é o slug que entra no SPIFFE ID e
                    o que o operador vê no `kubectl`; o nome é livre e pode repetir.
                    Mostrar o nome aqui e o slug lá faria a mesma coluna dizer coisas
                    diferentes em telas vizinhas. */}
                {multiSite && identidade.site_slug ? (
                  <Chip>{identidade.site_slug}</Chip>
                ) : null}
              </span>
              <span className={s.itemBase}>
                {motivo ? (
                  <span className={s.itemMotivo} data-tone="danger">
                    {motivo}
                  </span>
                ) : (
                  <span className={s.itemContrato}>
                    {identidade.active_contract_version
                      ? t('common.identityPane.contract', {
                          version: identidade.active_contract_version,
                        })
                      : t('common.identityPane.noContract')}
                  </span>
                )}
                {identidade.state ? (
                  <IdentityStateBadge state={identidade.state} tip={false} />
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
